import { useMemo } from 'react';
import { clsx } from 'clsx';
import { Server, Trash2, ChevronRight, Zap } from 'lucide-react';
import {
  ContainerRack,
  DatacenterNode,
  InfrastructureUnit,
  getRackDimensions,
  NodeType,
} from '../../types/configurator';
import { Button } from '../ui';

interface RackDetailPanelProps {
  rack: ContainerRack;
  nodes: DatacenterNode[];
  infrastructure: InfrastructureUnit[];
  allNodes: DatacenterNode[];  // All nodes that can be assigned
  allRacks: ContainerRack[];   // All racks to check for assignments
  onRackUpdate: (updates: Partial<ContainerRack>) => void;
  onRackRemove: () => void;
  onAssignNode: (nodeId: string) => void;
  onUnassignNode: (nodeId: string) => void;
}

const NODE_COLORS: Record<NodeType, { bg: string; text: string }> = {
  gateway: { bg: 'bg-orange-100', text: 'text-orange-700' },
  compute: { bg: 'bg-blue-100', text: 'text-blue-700' },
  storage: { bg: 'bg-green-100', text: 'text-green-700' },
  daq: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

export function RackDetailPanel({
  rack,
  nodes,
  infrastructure,
  allNodes,
  allRacks,
  onRackUpdate,
  onRackRemove,
  onAssignNode,
  onUnassignNode,
}: RackDetailPanelProps) {
  const dims = getRackDimensions(rack.type);

  // Calculate usage
  const { usedUnits, totalPower, unassignedNodes } = useMemo(() => {
    let used = 0;
    let power = 0;

    for (const node of nodes) {
      used += node.specs.rackUnits || 1;
      power += node.specs.powerWatts + (node.specs.gpu?.tdpWatts || 0);
    }

    // Add rack-mounted infrastructure
    for (const infra of infrastructure) {
      if (infra.rackId === rack.id && infra.specs.rackUnits) {
        used += infra.specs.rackUnits;
      }
      if (infra.rackId === rack.id) {
        power += infra.specs.powerWatts;
      }
    }

    // Find unassigned nodes - check ALL racks, not just current one
    const allAssignedIds = new Set<string>();
    for (const r of allRacks) {
      for (const nodeId of r.nodes) {
        allAssignedIds.add(nodeId);
      }
    }
    const unassigned = allNodes.filter((n) => !allAssignedIds.has(n.id));

    return { usedUnits: used, totalPower: power, unassignedNodes: unassigned };
  }, [nodes, infrastructure, rack, allNodes, allRacks]);

  const usagePercent = (usedUnits / dims.units) * 100;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-3">
        <div className="flex items-center gap-2">
          <Server className="h-5 w-5 text-gray-500" />
          <input
            type="text"
            value={rack.name}
            onChange={(e) => onRackUpdate({ name: e.target.value })}
            className="border-none bg-transparent text-sm font-medium focus:outline-none focus:ring-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {rack.type === 'rack-42' ? '42U' : '18U'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRackRemove}
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 border-b border-gray-200 p-3">
        {/* Space usage */}
        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-xs text-gray-500">Space</span>
            <span className="text-sm font-medium">
              {usedUnits}/{dims.units}U
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Power */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Zap className="h-3 w-3" />
              Power
            </span>
            <span className="text-sm font-medium">{totalPower}W</span>
          </div>
        </div>
      </div>

      {/* Assigned Nodes */}
      <div className="border-b border-gray-200 p-3">
        <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">
          Assigned Nodes ({nodes.length})
        </h4>
        {nodes.length === 0 ? (
          <p className="text-xs text-gray-400">No nodes assigned</p>
        ) : (
          <div className="space-y-1">
            {nodes.map((node) => {
              const colors = NODE_COLORS[node.type];
              return (
                <div
                  key={node.id}
                  className={clsx(
                    'flex items-center justify-between rounded px-2 py-1',
                    colors.bg
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className={clsx('text-xs font-medium', colors.text)}>
                      {node.hostname}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {node.specs.rackUnits || 1}U
                    </span>
                  </div>
                  <button
                    onClick={() => onUnassignNode(node.id)}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Node */}
      {unassignedNodes.length > 0 && (
        <div className="p-3">
          <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Add Node</h4>
          <div className="space-y-1">
            {unassignedNodes.slice(0, 5).map((node) => {
              const colors = NODE_COLORS[node.type];
              return (
                <button
                  key={node.id}
                  onClick={() => onAssignNode(node.id)}
                  className="flex w-full items-center justify-between rounded border border-gray-200 px-2 py-1 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <span className={clsx('text-xs', colors.text)}>{node.hostname}</span>
                    <span className="text-[10px] text-gray-400">
                      {node.specs.rackUnits || 1}U
                    </span>
                  </div>
                  <ChevronRight className="h-3 w-3 text-gray-400" />
                </button>
              );
            })}
            {unassignedNodes.length > 5 && (
              <p className="text-center text-[10px] text-gray-400">
                +{unassignedNodes.length - 5} more nodes
              </p>
            )}
          </div>
        </div>
      )}

      {/* Rack-mounted Infrastructure */}
      {infrastructure.filter((i) => i.rackId === rack.id).length > 0 && (
        <div className="border-t border-gray-200 p-3">
          <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">
            Rack Infrastructure
          </h4>
          <div className="space-y-1">
            {infrastructure
              .filter((i) => i.rackId === rack.id)
              .map((infra) => (
                <div
                  key={infra.id}
                  className="flex items-center justify-between rounded bg-gray-50 px-2 py-1"
                >
                  <span className="text-xs text-gray-600">{infra.name}</span>
                  <span className="text-[10px] text-gray-400">
                    {infra.specs.rackUnits}U
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Position info */}
      <div className="border-t border-gray-200 p-3 text-[10px] text-gray-400">
        Position: ({Math.round(rack.position.x)}mm, {Math.round(rack.position.y)}mm)
        {rack.rotation !== 0 && ` | Rotation: ${rack.rotation}°`}
      </div>
    </div>
  );
}
